import classnames from 'classnames'
import styles from './NavigationBar.module.scss'
import useStore from '@/store'
import Hamburger from '@/components/_svg/Hamburger'
import Link from '@/components/Link/Link'
import Logo from '@/components/_svg/Logo'
import { Fragment, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import useWindowResize from '@/hooks/use-window-resize'
import { classesToElements } from '@/utils'
import { HOME_SLUG, TRANSITION_DURATION } from '@/data'
import X from '@/components/_svg/X'
import useBreakpoint from '@/hooks/use-breakpoint'
import LogoWordmarkAlt from '@/components/_svg/LogoWordmarkAlt'

const CLASSES = {
  LINES: styles.line,
  DROPDOWN_CONTAINER: styles.dropdownContainer,
  DROPDOWN_CONTAINER_INNER: styles.dropdownContainer__inner,
  DROPDOWN_BOTTOM: styles.dropdownBottom,
  NAV_ITEMS: styles.navItem,
  NAV_ITEM_LINKS: styles.navItem__link,
  NAV_ITEM_COUNTS: styles.navItem__count,
  NAV_ITEM_LABELS: styles.navItem__label,
  ACTIVE_LABEL_CURRENT: styles.activeLinkLabel,
  ACTIVE_LABEL_INCOMING: styles.activeLabelIncoming,
  ICON_HAMBURGER: styles.hamburgerIcon,
  ICON_X: styles.xIcon,
}

const NavigationBar = ({ className }) => {
  const globalData = useStore(state => state.globalData)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)
  const navIsOpen = useStore(state => state.navIsOpen)
  const setAllowProductGalleryListHover = useStore(state => state.setAllowProductGalleryListHover)
  const gsapContext = useRef()
  const resizeKey = useWindowResize()
  const animations = useRef()
  const $container = useRef()
  const $refs = useRef({})
  const currentPageData = useStore(state => state.currentPageData)
  const prevPageRef = useRef()
  const tl = useRef()
  const [animationsCreated, setAnimationsCreated] = useState(false)
  const theme = useStore(state => state.theme)
  const { isMobile } = useBreakpoint()

  useEffect(() => {
    const currentTitle = currentPageData?.title
    if (!currentTitle || !animations?.current?.animateInBarTitle || !animationsCreated) return
    animations.current.animateInBarTitle(currentTitle)
  }, [currentPageData, resizeKey, animationsCreated])

  useEffect(() => {
    if (!$container.current) return
    if (gsapContext.current) gsapContext.current.revert()

    gsapContext.current = gsap.context(() => {
      $refs.current = classesToElements(CLASSES, $container.current)

      const resetDropdown = () => {
        gsap.set($refs.current.LINES, {
          scaleX: 0,
          transformOrigin: 'left center',
        })

        gsap.set([...$refs.current.NAV_ITEM_COUNTS, $refs.current.NAV_ITEM_LABELS], {
          y: '150%',
        })

        gsap.set($refs.current.DROPDOWN_BOTTOM, {
          autoAlpha: 0,
        })

        gsap.set($refs.current.DROPDOWN_CONTAINER, {
          height: 0,
        })
      }

      const dropdown = (open = true) => {
        const duration = open ? 1 : 0.5

        const dropdownHeight = $refs.current.DROPDOWN_CONTAINER_INNER.offsetHeight

        gsap.killTweensOf($refs.current.DROPDOWN_CONTAINER)
        gsap.to($refs.current.DROPDOWN_CONTAINER, {
          height: open ? dropdownHeight : 0,
          duration,
          ease: 'Power4.easeOut',
        })

        $refs.current.NAV_ITEM_COUNTS.forEach(($el, i) => {
          const $label = $refs.current.NAV_ITEM_LABELS[i]
          if (!$el || !$label) return
          gsap.killTweensOf([$el, $label])
          gsap.to([$el, $label], {
            y: open ? 0 : '150%',
            duration,
            ease: 'Power4.easeOut',
            delay: open ? 0.15 * i : 0,
          })
        })

        gsap.killTweensOf($refs.current.DROPDOWN_BOTTOM)
        gsap.to($refs.current.DROPDOWN_BOTTOM, {
          autoAlpha: open ? 1 : 0,
          delay: open ? duration * 0.44 : 0,
          duration,
          ease: 'Power4.easeOut',
        })

        gsap.killTweensOf($refs.current.LINES)
        gsap.to($refs.current.LINES, {
          scaleX: open ? 1 : 0,
          duration,
          ease: 'Power4.easeOut',
          stagger: open ? 0.08 : 0,
        })
      }

      const navItemHover = (index, isActive) => {
        if (!index) return
        const $label = $refs.current.NAV_ITEM_LABELS[index]
        if (!$label) return
        gsap.killTweensOf($label, 'x,opacity')
        gsap.to($label, {
          x: isActive ? '0.5rem' : 0,
          duration: isActive ? 0.6 : 0.4,
          opacity: isActive ? 0.5 : 1,
          ease: 'Power4.easeOut',
        })
      }

      const animateIcons = navIsOpen => {
        gsap.killTweensOf([$refs.current.ICON_HAMBURGER, $refs.current.ICON_X])
        const duration = 0.4
        gsap.to($refs.current.ICON_HAMBURGER, {
          opacity: navIsOpen ? 0 : 1,
          // delay: navIsOpen ? 0 : duration,
          rotate: navIsOpen ? 45 : 0,
          duration,
          ease: 'back.out',
        })
        gsap.to($refs.current.ICON_X, {
          opacity: !navIsOpen ? 0 : 1,
          // delay: !navIsOpen ? 0 : duration,
          rotate: !navIsOpen ? 45 : 0,
          duration,
          ease: 'back.out',
        })
      }

      const animateInBarTitle = title => {
        const { ACTIVE_LABEL_CURRENT: $current, ACTIVE_LABEL_INCOMING: $incoming } = $refs.current
        if (!$current || !$incoming) return
        if (tl.current) tl.current.kill()

        tl.current = gsap.timeline({
          onComplete: () => {
            prevPageRef.current = title
            gsap.set([$current, $incoming], { clearProps: 'all' })

            $incoming.innerHTML = ''
            $current.innerHTML = title

            tl.current.kill()
          },
        })

        $incoming.innerHTML = title

        const isInitial = !prevPageRef.current
        const duration = isInitial ? 0.001 : TRANSITION_DURATION * 0.5

        tl.current.to($current, {
          y: '-100%',
          duration,
          ease: 'Power4.easeInOut',
        })

        tl.current.to(
          $incoming,
          {
            y: 0,
            duration,
            ease: 'Power4.easeInOut',
          },
          0,
        )
      }

      animations.current = {
        resetDropdown,
        navItemHover,
        dropdown,
        animateIcons,
        animateInBarTitle,
      }

      setAnimationsCreated(true)
    }, $container.current)
  }, [resizeKey])

  useEffect(() => {
    return () => {
      if (gsapContext.current) gsapContext.current.revert()
    }
  }, [])

  useEffect(() => {
    if (!animations.current) return
    animations.current.resetDropdown()
  }, [resizeKey, animations])

  useEffect(() => {
    if (!animations.current) return
    animations.current.dropdown(navIsOpen)
    animations.current.animateIcons(navIsOpen)
  }, [navIsOpen, animations])

  const handleLinkFocus = () => {
    if (isMobile) return
    if (!navIsOpen) setNavIsOpen(true)
  }

  if (
    !globalData?.navigation?.links?.length ||
    !globalData?.navigation?.email ||
    !globalData?.navigation?.instagramLink
  ) {
    return null
  }

  return (
    <div
      ref={$container}
      className={classnames(styles.NavigationBar, className)}
      onMouseEnter={() => {
        if (isMobile) return
        setNavIsOpen(true)
        setAllowProductGalleryListHover(false)
      }}
      onMouseLeave={() => {
        if (isMobile) return
        setNavIsOpen(false)
        setAllowProductGalleryListHover(true)
      }}
      data-theme={theme}
    >
      <div className={styles.bg} />
      <div className={styles.blur} />
      <div className={styles.inner}>
        <div className={styles.activeLinkContainer}>
          {isMobile && (
            <Link
              link={{
                linkType: 'internal',
                link: {
                  _type: 'page',
                  slug: HOME_SLUG,
                },
              }}
              className={styles.logoLinkMobile}
            >
              <LogoWordmarkAlt className={styles.logoLinkMobile__logo} />
            </Link>
          )}
          <button
            className={styles.toggleButton}
            aria-label={`${navIsOpen ? 'Close' : 'Open'} navigation menu`}
            onClick={() => {
              setNavIsOpen(!navIsOpen)
            }}
          >
            <Hamburger className={CLASSES.ICON_HAMBURGER} />
            <X className={CLASSES.ICON_X} />
          </button>
          <div className={styles.activePageContainer}>
            <div className={CLASSES.ACTIVE_LABEL_CURRENT}></div>
            <div className={CLASSES.ACTIVE_LABEL_INCOMING}></div>
          </div>
        </div>
        <div className={CLASSES.DROPDOWN_CONTAINER}>
          <div className={CLASSES.DROPDOWN_CONTAINER_INNER}>
            <ul className={styles.nav__list}>
              {globalData?.navigation?.links?.map((link, i) => {
                return (
                  <Fragment key={i}>
                    <li className={CLASSES.LINES} />
                    <li className={CLASSES.NAV_ITEMS}>
                      <Link
                        onFocus={handleLinkFocus}
                        className={CLASSES.NAV_ITEM_LINKS}
                        link={link}
                        onMouseEnter={() => {
                          if (isMobile) return
                          animations.current.navItemHover(i, true)
                        }}
                        onMouseLeave={() => {
                          if (isMobile) return
                          animations.current.navItemHover(i, false)
                        }}
                        activeClass={styles.active}
                        disableCursorTextOnHover
                      >
                        <span
                          aria-hidden={true}
                          tabIndex="-1"
                          className={CLASSES.NAV_ITEM_COUNTS}
                        >
                          (0{i + 1})
                        </span>
                        <span className={CLASSES.NAV_ITEM_LABELS}>{link.label}</span>
                      </Link>
                    </li>
                  </Fragment>
                )
              })}
            </ul>
            <div className={CLASSES.LINES} />
            <div className={CLASSES.DROPDOWN_BOTTOM}>
              <div className={styles.dropdownBottom__email}>
                <Link
                  link={{
                    linkType: 'external',
                    link: `mailto:${globalData?.navigation?.email}`,
                    label: globalData?.navigation?.email,
                  }}
                  onFocus={handleLinkFocus}
                  className={styles.dropdownBottom__emailLink}
                  ariaLabel={`Email ${globalData?.navigation?.email}`}
                >
                  em.
                </Link>
              </div>
              <div className={styles.dropdownBottom__logo}>
                <Logo className={styles.dropdownBottom__logoIcon} />
              </div>
              <div className={styles.dropdownBottom__instagram}>
                <Link
                  link={{
                    linkType: 'external',
                    link: globalData?.navigation?.instagramLink,
                    label: 'Instagram',
                  }}
                  onFocus={handleLinkFocus}
                  ariaLabel={'Go to Instagram'}
                  className={styles.dropdownBottom__instagramLink}
                >
                  ig.
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

NavigationBar.displayName = 'NavigationBar'

export default NavigationBar
